package pl.decks.personal.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ColorInterpolationMethod
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.css.functions.colorMix
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.icons.fa.FaPlay
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import pl.decks.personal.components.layouts.blog.BlogPostData
import pl.decks.personal.components.layouts.site.SiteLayoutData
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.components.widgets.splitcard.SplitCardStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.blog.BlogPostCard
import pl.decks.personal.pages.blog.BlogPostEntry
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout


@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(SiteLayoutData("Home"))
}

@Page
@Layout(".components.layouts.site.SiteLayout")
@Composable
fun HomePage() = Column(HomePageStylesheet.Page.toModifier()) {
    Box(HomePageStylesheet.Front.toModifier(), contentAlignment = Alignment.Center) {
        HomePageContent()
    }
}

@Composable
private fun HomePageContent() = Column(
    modifier = Modifier.fillMaxWidth(),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.spacedBy(SiteLayout.Space.Large),
) {
    IntroWords()
    IEVPRedirect()
    BlogRedirect()
}

@Composable
private fun IntroWords() = Column(
    modifier = HomePageStylesheet.Intro.toModifier(),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    SpanText(
        "This webpage came into existence solely because I despise social media. ",
        modifier = HomePageStylesheet.Emphasis.toModifier(),
    )
    Span {
        Text("I worked in the data field long enough to develop an occupational aversion to oversharing. ")
        Text("Building a personal brand attached to a name is not what I aim for - it is enough that I can prove ownership when required.")
    }
}

@Composable
private fun IEVPRedirect() = Column(
    modifier = Modifier.fillMaxWidth(),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.spacedBy(SiteLayout.Space.Small),
) {
    Span {
        Text("Anyway, hiring requires credibility and this is my attempt to build some. ")
        Text("If you want to grasp how it would be to work with me, visit the IEVP Page.")
    }
    IEVPButton()
}

@Composable
private fun BlogRedirect() = Column(
    modifier = Modifier.fillMaxWidth(),
    horizontalAlignment = Alignment.CenterHorizontally,
    verticalArrangement = Arrangement.spacedBy(SiteLayout.Space.Small),
) {
    Span {
        Text("Leaving a ghost site isn't my beat either, so I decided to start blogging. ")
        Text("I have no idea where it will go. ")
    }
    BlogPseudoCard()
}

@Composable
private fun IEVPButton() = Link("/ievp", variant = UndecoratedLinkVariant) {
    Box(
        HomePageStylesheet.IEVPRedirect.toModifier(),
        contentAlignment = Alignment.Center,
    ) {
        FaPlay(HomePageStylesheet.IEVPPlayIcon.toModifier())
    }
}

@Composable
private fun BlogPseudoCard() = BlogPostCard(
    blogPost = BlogPostEntry(
        route = "/blog",
        blogPostData = BlogPostData(
            title = "Blog",
            description = "",
            heroImage = "/images/home/blog_list.png",
            date = null,
            author = "@me",
            tags = listOf(),
        ),
    ),
    selectedTags = listOf(),
    onBlogNavigated = { },
)

object HomePageStylesheet {
    object Vars {
        val pageAccentColor by StyleVariable<Color>()
        val playButtonSize by StyleVariable(12.cssRem)
        val playButtonIconSize by StyleVariable(9.cssRem)

        object Internal {
            val playButtonColor by StyleVariable<Color>()
        }

    }

    val Page = SiteLayoutStylesheet.Page.Content.extendedBy {
        base {
            Modifier
                .setVariable(Vars.pageAccentColor, colorMode.toSitePalette().accents.primary)
                .setVariable(Vars.playButtonSize, 12.cssRem)
                .setVariable(Vars.playButtonIconSize, 9.cssRem)
        }
        Breakpoint.MD {
            Modifier
                .setVariable(Vars.playButtonSize, 14.cssRem)
                .setVariable(Vars.playButtonIconSize, 11.cssRem)
        }
    }

    val Front = SiteLayoutStylesheet.Page.Content.extendedByBase {
        Modifier
            .fillMaxWidth()
            .minHeight(SiteLayoutStylesheet.Vars.approxScreenSpace.value())
            .setVariable(SplitCardStylesheet.Vars.cardAccentColor, colorMode.toSitePalette().accents.primary)
    }

    val Intro = FontTypography.sansSerif.extendedByBase {
        Modifier
            .fillMaxWidth()
            .textAlign(TextAlign.Center)
            .fontWeight(FontTypography.Weight.Light)
            .gap(SiteLayout.Space.XSmall)
    }
    val Emphasis = CssStyle.base {
        Modifier.fontWeight(FontTypography.Weight.Medium)
    }

    val IEVPRedirect = CssStyle {
        val accent = Vars.pageAccentColor.value()
        val background = colorMode.toPalette().background.toRgb()
        base {
            Modifier
                .size(Vars.playButtonSize.value())
                .border(SiteLayout.Line.Thick, LineStyle.Solid, accent)
                .borderRadius(SiteLayout.Rounding.Full)
                .setVariable(Vars.Internal.playButtonColor, accent)
                .transition {
                    property("background-color")
                    duration(TransitionDurationVars.Normal.value())
                    timingFunction(TransitionTimingFunction.EaseInOut)
                }
        }
        hover {
            Modifier
                .backgroundColor(
                    colorMix(
                        interpolation = ColorInterpolationMethod.Hsl,
                        color1 = accent to 75.percent,
                        color2 = Colors.Transparent to 25.percent,
                    )
                )
                .setVariable(Vars.Internal.playButtonColor, background)
        }
        active {
            Modifier
                .backgroundColor(accent)
                .setVariable(Vars.Internal.playButtonColor, background)
        }
    }

    val IEVPPlayIcon = CssStyle.base {
        Modifier
            .fontSize(Vars.playButtonIconSize.value())
            .color(Vars.Internal.playButtonColor.value())
            .translateX(8.percent)
            .transition {
                property("color")
                duration(TransitionDurationVars.VeryFast.value())
                timingFunction(TransitionTimingFunction.EaseInOut)
            }
    }
}
